/**
 * Drifts List Component
 *
 * Feature: 023-service-principal-registry
 * Task: T042 - Create DriftsList component showing active drifts with fix buttons
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Table displaying active permission drifts with fix/resolve actions
 */

import React, { useState } from 'react';
import { SpWorkspaceAssignment, DriftStatus } from '../types';
import { DriftStatusBadge, getDriftSeverity } from './DriftStatusBadge';
import { DualScrollTable, usePagination, PaginationControls } from '../../../components/common/TableControls';
import { Modal, Button } from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

interface DriftWithDetails extends SpWorkspaceAssignment {
  servicePrincipalName: string;
  workspaceName: string;
}

interface DriftsListProps {
  drifts: DriftWithDetails[];
  isLoading?: boolean;
  onFix?: (driftId: string) => Promise<void>;
  onResolve?: (driftId: string, resolution: string) => Promise<void>;
  fixingId?: string | null;
  resolvingId?: string | null;
}

/**
 * Resolution modal component
 */
interface ResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (resolution: string) => void;
  isSubmitting: boolean;
}

const ResolutionModal: React.FC<ResolutionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [resolution, setResolution] = useState('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='sm'
      title='Resolve Drift Manually'
      footer={
        <>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(resolution)}
            disabled={!resolution.trim() || isSubmitting}
          >
            {isSubmitting ? 'Resolving...' : 'Mark as Resolved'}
          </Button>
        </>
      }
    >
      <p className='text-sm text-[var(--color-text-secondary)] mb-4'>
        Please provide a resolution note explaining why this drift is being marked as resolved
        without fixing.
      </p>
      <textarea
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
        placeholder='e.g., Permission removed intentionally per request #1234'
        rows={3}
        className='w-full border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md px-3 py-2 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
        disabled={isSubmitting}
      />
    </Modal>
  );
};

/**
 * Loading skeleton row
 */
const SkeletonRow: React.FC = () => (
  <tr className='animate-pulse'>
    <td className='px-4 py-3'>
      <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-32'></div>
    </td>
    <td className='px-4 py-3'>
      <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-40'></div>
    </td>
    <td className='px-4 py-3'>
      <div className='h-6 bg-[var(--color-bg-subtle)] rounded w-28'></div>
    </td>
    <td className='px-4 py-3'>
      <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-20'></div>
    </td>
    <td className='px-4 py-3'>
      <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-24'></div>
    </td>
    <td className='px-4 py-3'>
      <div className='h-8 bg-[var(--color-bg-subtle)] rounded w-32'></div>
    </td>
  </tr>
);

/**
 * Drifts List Component
 */
export const DriftsList: React.FC<DriftsListProps> = ({
  drifts,
  isLoading = false,
  onFix,
  onResolve,
  fixingId,
  resolvingId,
}) => {
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [selectedDriftId, setSelectedDriftId] = useState<string | null>(null);

  // Sort by severity (critical first)
  const sortedDrifts = [...drifts].sort(
    (a, b) => getDriftSeverity(b.driftStatus) - getDriftSeverity(a.driftStatus)
  );

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(sortedDrifts);

  const handleResolveClick = (driftId: string) => {
    setSelectedDriftId(driftId);
    setResolveModalOpen(true);
  };

  const handleResolveSubmit = async (resolution: string) => {
    if (selectedDriftId && onResolve) {
      await onResolve(selectedDriftId, resolution);
      setResolveModalOpen(false);
      setSelectedDriftId(null);
    }
  };

  if (isLoading) {
    return (
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow border border-[var(--color-border)] overflow-hidden'>
        <div className='p-4 border-b border-[var(--color-border)]'>
          <h3 className='text-lg font-semibold text-[var(--color-text-primary)]'>Active Drifts</h3>
        </div>
        <table className='min-w-full divide-y divide-[var(--color-border)]'>
          <thead className='bg-[var(--color-bg-subtle)]'>
            <tr>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Service Principal
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Workspace
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Drift Status
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Expected
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Detected
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
            {[...Array(3)].map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (drifts.length === 0) {
    return (
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow border border-[var(--color-border)] p-8 text-center'>
        <div className='text-green-500 text-4xl mb-4'>✅</div>
        <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-1'>No Active Drifts</h3>
        <p className='text-[var(--color-text-secondary)]'>All service principal permissions are in sync.</p>
      </div>
    );
  }

  return (
    <>
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow border border-[var(--color-border)] overflow-hidden'>
        <div className='p-4 border-b border-[var(--color-border)] flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-semibold text-[var(--color-text-primary)]'>Active Drifts</h3>
            <p className='text-sm text-[var(--color-text-secondary)]'>
              {drifts.length} drift{drifts.length !== 1 ? 's' : ''} detected
            </p>
          </div>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'>
            ⚠️ {drifts.length} requiring attention
          </span>
        </div>

        <DualScrollTable>
          <table className='min-w-full divide-y divide-[var(--color-border)]'>
            <thead className='bg-[var(--color-bg-subtle)]'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Service Principal
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Workspace
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Drift Status
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Permission
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Last Verified
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
              {pageItems.map((drift) => {
                const isFixing = fixingId === drift.id;
                const isResolving = resolvingId === drift.id;
                const canFix = drift.driftStatus === 'PERMISSION_MISSING' && drift.expected;

                return (
                  <tr key={drift.id} className='hover:bg-[var(--color-bg-subtle)]'>
                    <td className='px-4 py-3'>
                      <span className='font-medium text-[var(--color-text-primary)]'>
                        {drift.servicePrincipalName}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm text-[var(--color-text-secondary)]'>{drift.workspaceName}</td>
                    <td className='px-4 py-3'>
                      <DriftStatusBadge status={drift.driftStatus} size='sm' />
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          drift.expected ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                        }`}
                      >
                        {drift.permissionLevel}
                      </span>
                      {drift.expected && (
                        <span className='ml-1 text-xs text-[var(--color-text-secondary)]'>(expected)</span>
                      )}
                    </td>
                    <td className='px-4 py-3 text-sm text-[var(--color-text-secondary)]'>
                      {drift.lastVerifiedAt
                        ? formatEastern(drift.lastVerifiedAt)
                        : 'Never'}
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex items-center space-x-2'>
                        {canFix && onFix && (
                          <button
                            onClick={() => onFix(drift.id)}
                            disabled={isFixing || isResolving}
                            className='inline-flex items-center px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50'
                          >
                            {isFixing ? (
                              <>
                                <span className='animate-spin mr-1'>⏳</span>
                                Fixing...
                              </>
                            ) : (
                              <>
                                <span className='mr-1'>🔧</span>
                                Fix
                              </>
                            )}
                          </button>
                        )}
                        {onResolve && (
                          <button
                            onClick={() => handleResolveClick(drift.id)}
                            disabled={isFixing || isResolving}
                            className='inline-flex items-center px-2.5 py-1 text-xs font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] rounded hover:bg-[var(--color-bg-subtle)] disabled:opacity-50'
                          >
                            {isResolving ? (
                              <>
                                <span className='animate-spin mr-1'>⏳</span>
                                Resolving...
                              </>
                            ) : (
                              <>
                                <span className='mr-1'>✔️</span>
                                Resolve
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DualScrollTable>
        <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
      </div>

      <ResolutionModal
        isOpen={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false);
          setSelectedDriftId(null);
        }}
        onSubmit={handleResolveSubmit}
        isSubmitting={!!resolvingId}
      />
    </>
  );
};

export default DriftsList;
